import { useEffect } from "react";
import { Droplets, Bike, Sparkles, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal, Overline } from "@/components/Reveal";

const VALUES = [
  {
    icon: Sparkles,
    title: "Hand-finished, always",
    text: "No machines, no shortcuts. Every pair is cleaned by hand, inspected under light, and only leaves when we'd wear it ourselves.",
  },
  {
    icon: Bike,
    title: "Door to door",
    text: "Free local collection and delivery within our catchment, or drop your pair at a partner point. You never queue, post or wait in.",
  },
  {
    icon: MapPin,
    title: "Local & proud",
    text: "A small independent studio serving our own neighbourhoods. Your trainers never leave the area — and neither do we.",
  },
];

const PRIVACY_SECTIONS = [
  {
    title: "Who we are",
    text: "Clean & Revive is the data controller for the personal information you give us through this website. You can reach us at any time at cleanandrevive@gmail.com or on 07817 234560 about anything in this policy.",
  },
  {
    title: "What we collect",
    text: "Your name, email address and password (stored only as a secure hash) when you create an account; your address, postcode, preferred dates and notes when you book; and your booking history. We never see or store your card details.",
  },
  {
    title: "How we use it",
    text: "To collect, clean and return your trainers; to send booking confirmations and stage updates by email; to take payment; and to keep your account and booking history available to you. We do not use your information for advertising, and we never sell it.",
  },
  {
    title: "Payments",
    text: "Online payments are processed securely by Stripe. Your card details go directly to Stripe and never touch our servers — we only receive confirmation that a payment succeeded, along with a transaction reference.",
  },
  {
    title: "Cookies & storage",
    text: "We use a single secure sign-in cookie (and your browser's local storage) to keep you logged in. That's all — no advertising cookies, no tracking pixels, no analytics beacons.",
  },
  {
    title: "Who we share it with",
    text: "Only the services needed to run Clean & Revive: Stripe (payments) and our transactional email provider (booking emails). Both process your data under their own strict terms and only on our instructions.",
  },
  {
    title: "How long we keep it",
    text: "Account and booking records are kept while your account is active so you can track and rebook. Ask us to delete your account and we will remove your personal details, keeping only what the law requires for tax and accounting records.",
  },
  {
    title: "Your rights",
    text: "Under UK GDPR you can ask for a copy of your data, correct it, delete it, restrict how we use it, or object to its use. Email cleanandrevive@gmail.com and we'll respond within 30 days. You can also complain to the ICO at ico.org.uk if you're ever unhappy.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, []);

  return (
    <main className="bg-obsidian" data-testid="about-page">
      <Navbar />

      <section className="mx-auto max-w-7xl px-6 pb-20 pt-44 lg:px-12">
        <Reveal>
          <Overline data-testid="about-overline">The studio</Overline>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tighter text-white sm:text-5xl lg:text-6xl">
            We bring trainers<br />
            <span className="text-outline-aqua">back to life.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
            Clean & Revive started with a simple frustration: great trainers were being retired early because
            no one cleaned them properly. So we built a studio that does — hand-cleaning every pair, then
            collecting and delivering them so the whole thing takes you sixty seconds.
          </p>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
            From everyday beaters to grails, every pair gets the same treatment: inspected, deep-cleaned,
            conditioned, protected, and returned boxed. 100+ pairs revived and counting.
          </p>
        </Reveal>

        <div className="mt-20 grid gap-8 md:grid-cols-3 lg:gap-10">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.12}>
              <div className="glow-card h-full border border-white/5 bg-carbon p-8" data-testid={`about-value-${i + 1}`}>
                <v.icon size={22} className="text-aqua" />
                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-white">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="privacy" data-testid="privacy-section" className="border-t border-white/5 bg-carbon/40">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-12">
          <Reveal>
            <Overline>Privacy policy</Overline>
            <h2 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tighter text-white sm:text-4xl">
              Your data, kept clean too.
            </h2>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              Last updated — August 2026
            </p>
          </Reveal>

          <div className="mt-14 grid gap-px border border-white/5 bg-white/5 md:grid-cols-2">
            {PRIVACY_SECTIONS.map((s, i) => (
              <div key={s.title} className="bg-obsidian p-8 sm:p-10" data-testid={`privacy-item-${i + 1}`}>
                <p className="font-mono text-xs tracking-[0.3em] text-aqua">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-display text-lg font-bold uppercase tracking-tight text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.text}</p>
              </div>
            ))}
          </div>

          <Reveal>
            <p className="mt-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              <Droplets size={14} className="text-aqua" />
              Questions — cleanandrevive@gmail.com — 07817 234560
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
